import StickyTableResponsive from '../../../components/common/StickyTableResponsive';
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Icon } from '@iconify/react';
import MasterLayout from '../../../masterLayout/MasterLayout';
import Breadcrumb from '../../../components/Breadcrumb';
import klubService from '../../../services/klubService';
import { notify } from '../../../components/common/AppToast';
import { ROUTES } from '../../../config/routes';
import { WORKFLOW_STATUS_LABELS } from '../../../utils/klubUtils';
import { AppAlert, AppButton, AppSpinner } from '../../../components/ui';
import '../../../styles/klub-fiche.scss';

const STATUS_OPTIONS = Object.keys(WORKFLOW_STATUS_LABELS);

const DISPLAY_GROUP_OPTIONS = [
  { value: 'vente', label: "Intégration · Process de vente" },
  { value: 'fabrication', label: "Intégration · Process de fabrication" },
  { value: 'mise_en_ligne', label: 'Actifs · Mise en ligne' },
  { value: 'resiliation', label: 'Résiliation' },
];

function buildStepForm(step) {
  const responsible = step.use_adherent_commercial
    ? '__commercial__'
    : (step.default_responsible_user_id ? String(step.default_responsible_user_id) : '');

  return {
    id: step.id,
    step_code: step.step_code,
    step_order: step.step_order,
    label: step.label ?? '',
    card_hint: step.card_hint ?? '',
    default_status: step.default_status ?? 'pending',
    responsible,
    excel_col: step.excel_col ?? '',
    display_group: step.display_group ?? '',
  };
}

function buildFormFromTemplate(template) {
  return {
    name: template.name ?? '',
    steps: [...(template.steps ?? [])]
      .sort((a, b) => a.step_order - b.step_order)
      .map(buildStepForm),
  };
}

const KlubAdminPipelineTemplatePage = () => {
  const [template, setTemplate] = useState(null);
  const [form, setForm] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [tpl, klubUsers] = await Promise.all([
        klubService.getPipelineTemplate(),
        klubService.listUsers(),
      ]);
      setTemplate(tpl);
      setForm(buildFormFromTemplate(tpl));
      setUsers(klubUsers);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleNameChange = (value) => {
    setForm((prev) => ({ ...prev, name: value }));
  };

  const handleStepChange = (index, field, value) => {
    setForm((prev) => {
      const steps = [...prev.steps];
      steps[index] = { ...steps[index], [field]: value };
      return { ...prev, steps };
    });
  };

  const resetForm = () => {
    if (template) {
      setForm(buildFormFromTemplate(template));
      setError(null);
    }
  };

  const handleSave = async () => {
    if (!template || !form) return;
    setSaving(true);
    setError(null);
    try {
      const payload = {
        name: form.name.trim(),
        steps: form.steps.map((step) => {
          const data = {
            id: step.id,
            label: step.label.trim(),
            card_hint: step.card_hint.trim(),
            default_status: step.default_status,
            step_order: Number(step.step_order),
          };
          if (step.responsible === '__commercial__') {
            data.use_adherent_commercial = true;
            data.default_responsible_user_id = null;
          } else if (step.responsible) {
            data.use_adherent_commercial = false;
            data.default_responsible_user_id = Number(step.responsible);
          } else {
            data.use_adherent_commercial = false;
            data.default_responsible_user_id = null;
          }
          if (step.display_group) {
            data.display_group = step.display_group;
          }
          return data;
        }),
      };

      const updated = await klubService.updatePipelineTemplate(template.id, payload);
      setTemplate(updated);
      setForm(buildFormFromTemplate(updated));
      notify.success('Template pipeline enregistré');
    } catch (err) {
      notify.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <MasterLayout>
        <AppSpinner />
      </MasterLayout>
    );
  }

  if (!template || !form) {
    return (
      <MasterLayout>
        <AppAlert variant="danger">Template introuvable</AppAlert>
      </MasterLayout>
    );
  }

  return (
    <MasterLayout>
      <div className="klub-fiche">
        <Breadcrumb title="Template pipeline Klub" hideHeading />

        <div className="kf-head">
          <div>
            <h1 className="kf-title" style={{ fontSize: 24 }}>Template pipeline</h1>
            <p className="kf-section-sub mb-0">
              Modèle appliqué aux nouveaux adhérents — les fiches existantes conservent leurs étapes personnalisées.
            </p>
          </div>
          <AppButton
            as={Link}
            to={ROUTES.klub.admin}
            size="sm"
            variant="outline-secondary"
            icon="mdi:arrow-left"
          >
            Retour
          </AppButton>
        </div>

        {error && <div className="kf-alert kf-alert--error">{error}</div>}

        <div className="kf-section">
          <div className="kf-section-head">
            <div className="kf-section-icon">
              <Icon icon="mdi:cog-outline" />
            </div>
            <div>
              <h5 className="kf-section-title">Paramètres généraux</h5>
              <p className="kf-section-sub">Nom du template actif (ID {template.id})</p>
            </div>
          </div>
          <div className="kf-section-body">
            <div className="kf-grid">
              <div className="kf-field kf-col-6">
                <label className="kf-label">Nom du template</label>
                <input
                  className="kf-input"
                  value={form.name}
                  onChange={(e) => handleNameChange(e.target.value)}
                />
              </div>
              <div className="kf-field kf-col-6">
                <label className="kf-label">Dernière mise à jour</label>
                <input className="kf-input" value={template.updated_at ? new Date(template.updated_at).toLocaleString('fr-FR') : '—'} disabled />
              </div>
            </div>
          </div>
        </div>

        <div className="kf-section">
          <div className="kf-section-head">
            <div className="kf-section-icon">
              <Icon icon="mdi:stairs" />
            </div>
            <div>
              <h5 className="kf-section-title">Étapes du pipeline</h5>
              <p className="kf-section-sub">{form.steps.length} sous-étapes — à rattacher aux 3 phases (Intégration, Actifs, Résiliation)</p>
            </div>
          </div>
          <div className="kf-section-body p-0">
            <StickyTableResponsive>
              <table className="table mb-0 align-middle">
                <thead className="bg-neutral-50">
                  <tr>
                    <th style={{ width: 70 }}>Ordre</th>
                    <th style={{ width: 140 }}>Code</th>
                    <th>Libellé</th>
                    <th>Phrase carte</th>
                    <th style={{ minWidth: 200 }}>Responsable par défaut</th>
                    <th style={{ width: 160 }}>Groupe affichage</th>
                    <th style={{ width: 150 }}>Statut initial</th>
                    <th style={{ width: 70 }}>Excel</th>
                  </tr>
                </thead>
                <tbody>
                  {form.steps.map((step, index) => (
                    <tr key={step.id}>
                      <td>
                        <input
                          type="number"
                          min="1"
                          className="kf-input"
                          style={{ padding: '8px 10px', width: 64 }}
                          value={step.step_order}
                          onChange={(e) => handleStepChange(index, 'step_order', e.target.value)}
                        />
                      </td>
                      <td>
                        <code className="text-xs text-secondary-light">{step.step_code}</code>
                      </td>
                      <td>
                        <input
                          className="kf-input"
                          style={{ padding: '8px 12px' }}
                          value={step.label}
                          onChange={(e) => handleStepChange(index, 'label', e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          className="kf-input"
                          style={{ padding: '8px 12px' }}
                          value={step.card_hint}
                          onChange={(e) => handleStepChange(index, 'card_hint', e.target.value)}
                        />
                      </td>
                      <td>
                        <select
                          className="kf-select"
                          style={{ padding: '8px 12px' }}
                          value={step.responsible}
                          onChange={(e) => handleStepChange(index, 'responsible', e.target.value)}
                        >
                          <option value="">— Non assigné —</option>
                          <option value="__commercial__">Commercial du client</option>
                          {users.map((u) => (
                            <option key={u.id} value={String(u.id)}>{u.full_name}</option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <select
                          className="kf-select"
                          style={{ padding: '8px 12px' }}
                          value={step.display_group}
                          onChange={(e) => handleStepChange(index, 'display_group', e.target.value)}
                        >
                          <option value="">—</option>
                          {DISPLAY_GROUP_OPTIONS.map((g) => (
                            <option key={g.value} value={g.value}>{g.label}</option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <select
                          className="kf-select"
                          style={{ padding: '8px 12px' }}
                          value={step.default_status}
                          onChange={(e) => handleStepChange(index, 'default_status', e.target.value)}
                        >
                          {STATUS_OPTIONS.map((s) => (
                            <option key={s} value={s}>{WORKFLOW_STATUS_LABELS[s]}</option>
                          ))}
                        </select>
                      </td>
                      <td className="text-center text-secondary-light text-sm">
                        {step.excel_col || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </StickyTableResponsive>
          </div>
        </div>

        <div className="kf-section" style={{ marginBottom: 40 }}>
          <div className="kf-actions">
            <button type="button" className="kf-btn kf-btn-secondary" disabled={saving} onClick={resetForm}>
              Annuler
            </button>
            <button type="button" className="kf-btn kf-btn-success" disabled={saving} onClick={handleSave}>
              {saving ? 'Enregistrement…' : 'Enregistrer le template'}
            </button>
          </div>
        </div>
      </div>
    </MasterLayout>
  );
};

export default KlubAdminPipelineTemplatePage;
